import { useCallback, useEffect, useMemo, useState } from 'react';
import { PlatformProvider } from '@stans-world/sdk/react';
import { useAuth } from '../auth/AuthContext';
import { apiUrl } from '../apiOrigin';
import { registeredGameSlugs } from '../games/gameRegistry';

const PRIMARY_GAME_ID = registeredGameSlugs[0] || 'paper-fly';

async function parseJson(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return {};
  }
}

const EMPTY_PRIMARY_STATE = Object.freeze({
  bestDistance: 0,
  dailyBestDistance: 0,
  lastDistance: 0,
  runsCount: 0,
  loaded: false,
});

export function StansWorldPlatformProvider({ children }) {
  const { user, authChecked } = useAuth();
  const [leaderboards, setLeaderboards] = useState({
    daily: { rows: [], self: null },
    allTime: { rows: [], self: null },
  });
  const [primaryGameState, setPrimaryGameState] = useState(EMPTY_PRIMARY_STATE);

  const refreshLeaderboard = useCallback(async () => {
    try {
      const q = new URLSearchParams({ limit: '10', gameId: PRIMARY_GAME_ID });
      const res = await fetch(apiUrl(`/api/platform/leaderboard/best?${q}`), {
        credentials: 'include',
      });
      const data = await parseJson(res);
      if (res.ok && data.daily?.rows && data.allTime?.rows) {
        setLeaderboards({
          daily: { rows: data.daily.rows, self: data.daily.self ?? null },
          allTime: { rows: data.allTime.rows, self: data.allTime.self ?? null },
        });
      }
    } catch {
      /* network / CORS — avoid uncaught rejections on poll */
    }
  }, []);

  const refreshPrimaryGameState = useCallback(async () => {
    if (!user) {
      setPrimaryGameState(EMPTY_PRIMARY_STATE);
      return;
    }
    try {
      const res = await fetch(apiUrl(`/api/games/${PRIMARY_GAME_ID}/state`), {
        credentials: 'include',
      });
      const data = await parseJson(res);
      const s = data?.state;
      if (res.ok && s && typeof s === 'object') {
        setPrimaryGameState({
          bestDistance: Number(s.bestDistance) || 0,
          dailyBestDistance: Number(s.dailyBestDistance) || 0,
          lastDistance: Number(s.lastDistance) || 0,
          runsCount: Number(s.runsCount) || 0,
          loaded: true,
        });
      }
    } catch {
      /* network / CORS — avoid uncaught rejections on poll */
    }
  }, [user]);

  useEffect(() => {
    if (!authChecked) {
      return undefined;
    }
    refreshLeaderboard();
    refreshPrimaryGameState();
    const t = window.setInterval(() => {
      refreshLeaderboard();
      refreshPrimaryGameState();
    }, 4000);
    return () => window.clearInterval(t);
  }, [authChecked, refreshLeaderboard, refreshPrimaryGameState]);

  const gameApiUrl = useCallback((slug, path) => {
    const p = path.startsWith('/') ? path : `/${path}`;
    return apiUrl(`/api/games/${slug}${p}`);
  }, []);

  const getLeaderboard = useCallback(
    () => ({
      daily: { top: leaderboards.daily.rows, self: leaderboards.daily.self },
      allTime: { top: leaderboards.allTime.rows, self: leaderboards.allTime.self },
      /** @deprecated All-time only; prefer allTime.top */
      top: leaderboards.allTime.rows,
      self: leaderboards.allTime.self,
      refresh: refreshLeaderboard,
    }),
    [leaderboards, refreshLeaderboard],
  );

  const value = useMemo(
    () => ({
      user,
      authChecked,
      gameApiUrl,
      getLeaderboard,
      primaryGame: {
        id: PRIMARY_GAME_ID,
        state: primaryGameState,
        refresh: refreshPrimaryGameState,
      },
    }),
    [user, authChecked, gameApiUrl, getLeaderboard, primaryGameState, refreshPrimaryGameState],
  );

  return <PlatformProvider value={value}>{children}</PlatformProvider>;
}
